import { useModalStore } from '@stores/ModalStore';
import { useEffect, useRef } from 'react';
import { isApplePlatform, matchesShortcut, Shortcut } from './shortcuts';

interface UseKeyboardShortcutParams {
  /** An entry from `SHORTCUTS`. */
  shortcut: Shortcut;
  /**
   * Called with the raw event when the chord matches and the guards pass. The caller decides
   * whether to `preventDefault()`, because that decision is the policy: calling it asserts "we
   * handled this", and every path that does not handle the chord must leave it alone so the
   * browser's own behaviour survives.
   */
  onMatch: (event: KeyboardEvent) => void;
  /** When false the shortcut does nothing. Read fresh on each keydown, so it may change freely. */
  enabled?: boolean;
  /**
   * Fire even while a modal is open. Off by default, which is almost always what you want — a
   * modal covers the surface the shortcut acts on, and stealing the key there feels broken.
   * Set true only for a shortcut that belongs to a modal itself.
   */
  allowWhileModalOpen?: boolean;
}

/**
 * A window-level, app-wide keyboard shortcut.
 *
 * CAPTURE phase, which is load-bearing rather than incidental. React 19 attaches its listeners to
 * the root container and, for portals, to the portal container — and React's synthetic
 * `stopPropagation()` calls the NATIVE one. So any component that stops propagation in an
 * `onKeyDown` (MUI text fields do, to keep typing out of menu typeahead) kills the event before it
 * reaches `window` in the bubble phase. Capture runs ahead of all of that, so a shortcut
 * registered here fires no matter where focus currently is.
 *
 * The flip side of capture is that this sees the event BEFORE any modal, editor or menu that might
 * legitimately own the chord. Hence the modal guard, and hence leaving `preventDefault()` to the
 * caller.
 *
 * Do NOT use this for bare-letter or arrow-key shortcuts. Those need a "focus isn't in a text
 * field" guard and belong closer to the surface that owns them — see `useKeyboardEvents.ts` in
 * MomentMergeVideoPlayer. This hook is for modifier chords, where there is no such ambiguity.
 *
 * Requires `ModalProvider`, which is mounted globally in `Entry.tsx` and so is always available.
 */
export function useKeyboardShortcut({
  shortcut,
  onMatch,
  enabled = true,
  allowWhileModalOpen = false,
}: UseKeyboardShortcutParams): void {
  const modalStore = useModalStore();

  // Refs-to-latest so the effect can have empty deps. A long-lived listener must never call a
  // stale closure (see .claude/rules/stable-callbacks.md), and callers of this hook typically
  // re-render on every keystroke — re-registering the listener that often would be wasteful as
  // well as wrong.
  const onMatchRef = useRef(onMatch);
  onMatchRef.current = onMatch;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const shortcutRef = useRef(shortcut);
  shortcutRef.current = shortcut;
  const allowWhileModalOpenRef = useRef(allowWhileModalOpen);
  allowWhileModalOpenRef.current = allowWhileModalOpen;

  useEffect(() => {
    // The platform cannot change mid-session, so resolve it once per mount rather than running
    // two regexes on every keystroke in the app.
    const apple = isApplePlatform();

    const onKeyDown = (event: KeyboardEvent) => {
      if (!enabledRef.current) return;
      if (!matchesShortcut(event, shortcutRef.current, apple)) return;
      // No `preventDefault()` on this path: a modal is covering the surface, so the browser's own
      // handling of the chord is the right outcome. Read here rather than as a render dependency,
      // so callers need not be observers.
      if (!allowWhileModalOpenRef.current && modalStore.hasOpenModals) return;
      onMatchRef.current(event);
    };

    // `capture: true` on both calls — removeEventListener with a mismatched capture flag is a
    // silent no-op and would leak the listener.
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [modalStore]);
}
